import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { questions } from './data/questions';
import { calculateScores, getTierLabel } from './utils/scoring';
import TypeformStep from './components/TypeformStep';
import WelcomeStep from './components/WelcomeStep';
import ContactStep from './components/ContactStep';
import QuestionStep from './components/QuestionStep';
import ResultsSection from './components/ResultsSection';
import AdminDashboard from './components/AdminDashboard';
import { storeFormSubmission, trackUserProgress } from './lib/supabase';
import { trackStartAssessment, trackCompletedAssessment } from './utils/metaPixel';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight } = FiIcons;

function BrandHealthCheck() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: ''
  });
  const [responses, setResponses] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [calculatedResults, setCalculatedResults] = useState(null);
  const [autoAdvanceTimeout, setAutoAdvanceTimeout] = useState(null);
  const [sessionStartTime] = useState(Date.now()); // Track when user started
  const [questionTimestamps, setQuestionTimestamps] = useState({}); // Track time spent on each question
  const [contactFormShownAt, setContactFormShownAt] = useState(null); // Track when contact form was shown

  // Flatten questions for easier navigation
  const flatQuestions = [];
  const categoryMap = {};
  let questionIndex = 0;

  Object.entries(questions).forEach(([category, categoryQuestions]) => {
    categoryQuestions.forEach((question, idx) => {
      flatQuestions.push({
        ...question,
        category,
        categoryIndex: idx,
        globalIndex: questionIndex
      });
      categoryMap[questionIndex] = { category, index: idx };
      questionIndex++;
    });
  });

  // Welcome + Questions (Contact form is now after questions) - Updated for 10 questions
  const totalSteps = 1 + flatQuestions.length;

  // Track page load/visitor immediately
  useEffect(() => {
    const trackInitialVisit = async () => {
      // Generate a random visitor ID for anonymous tracking
      const visitorId = Math.random().toString(36).substring(2, 15);

      // Get basic device info for analytics
      const deviceInfo = {
        userAgent: navigator.userAgent,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        isMobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
        timestamp: new Date().toISOString(),
        sessionStartTime: sessionStartTime
      };

      const userData = {
        id: visitorId,
        name: null,
        email: null,
        company: null,
        role: null,
        progress: "Landing Page View",
        exit_point: "Landing Page",
        responses: {},
        completed: false,
        device_info: deviceInfo,
        step_timestamps: {
          landing_page: Date.now()
        }
      };

      const { success, data } = await trackUserProgress(userData);
      if (success && data) {
        setSessionId(data[0]?.id);
        console.log("Initial visit tracked with ID:", data[0]?.id);
      }
    };

    trackInitialVisit();
  }, [sessionStartTime]);

  // Track user progress periodically with enhanced data
  useEffect(() => {
    const trackProgress = async () => {
      if (!sessionId) return; // Skip if we don't have a session ID yet

      let progress = "";
      let exitPoint = "";
      const currentTime = Date.now();
      const timeSpent = Math.round((currentTime - sessionStartTime) / 1000); // seconds

      if (currentStep === 1) {
        progress = "Landing Page View";
        exitPoint = "Landing Page";
      } else if (showContactForm) {
        progress = "Contact Information (Post-Questions)";
        exitPoint = "Contact Information";
      } else if (currentStep > 1) {
        const questionGlobalIndex = currentStep - 2;
        const { category } = categoryMap[questionGlobalIndex] || {};
        if (category) {
          progress = `${category} - Question ${questionGlobalIndex + 1}`;
          exitPoint = `${category} - Question ${questionGlobalIndex + 1}`;
        }
      }

      // Create a snapshot of questions for tracking
      const questionsSnapshot = {};
      Object.entries(questions).forEach(([category, categoryQuestions]) => {
        questionsSnapshot[category] = categoryQuestions.map(q => ({
          question: q.text,
          options: q.options
        }));
      });

      // Enhanced tracking data
      const userData = {
        id: sessionId,
        name: formData.name || null,
        email: formData.email || null,
        company: formData.company || null,
        role: formData.role || null,
        progress,
        exit_point: exitPoint,
        responses: {
          raw: responses,
          questions: questionsSnapshot
        },
        completed: false,
        current_step: currentStep,
        total_steps: totalSteps,
        time_spent_seconds: timeSpent,
        questions_answered: Object.keys(responses).reduce((total, category) => 
          total + Object.keys(responses[category] || {}).length, 0
        ),
        completion_percentage: Math.round((currentStep / totalSteps) * 100),
        last_activity: new Date().toISOString()
      };

      await trackUserProgress(userData);
    };

    // Track progress immediately when step changes
    trackProgress();

    // Also set up a periodic tracker to catch when users abandon
    const intervalId = setInterval(trackProgress, 30000); // Every 30 seconds

    return () => clearInterval(intervalId);
  }, [currentStep, formData, responses, sessionId, showContactForm, sessionStartTime, totalSteps]);

  // Clear auto-advance timeout when component unmounts or step changes
  useEffect(() => {
    return () => {
      if (autoAdvanceTimeout) {
        clearTimeout(autoAdvanceTimeout);
      }
    };
  }, [currentStep, autoAdvanceTimeout]);

  // Track when each step is entered
  useEffect(() => {
    if (currentStep > 1 && !showContactForm) {
      const questionGlobalIndex = currentStep - 2;
      if (questionGlobalIndex >= 0) {
        setQuestionTimestamps(prev => ({
          ...prev,
          [`question_${questionGlobalIndex + 1}`]: Date.now()
        }));
      }
    }
  }, [currentStep, showContactForm]);

  const handleAnswerChange = (questionGlobalIndex, value) => {
    const { category, index } = categoryMap[questionGlobalIndex];

    // Calculate time spent on this question
    const questionKey = `question_${questionGlobalIndex + 1}`;
    const timeSpent = questionTimestamps[questionKey]
      ? Math.round((Date.now() - questionTimestamps[questionKey]) / 1000)
      : 0;

    setResponses(prev => ({
      ...prev,
      [category]: {
        ...(prev[category] || {}),
        [index]: value
      }
    }));

    // Track the answer with timestamp
    setQuestionTimestamps(prev => ({
      ...prev,
      [`${questionKey}_completed`]: Date.now(),
      [`${questionKey}_time_spent`]: timeSpent
    }));

    // Auto-advance to next question after selection (but still allow manual next)
    const timeoutId = setTimeout(() => {
      handleNext();
    }, 1200); // Increased delay to give users time to click next manually

    setAutoAdvanceTimeout(timeoutId);
  };

  const validateEmail = (email) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  const canProceed = () => {
    if (currentStep === 1) return true; // Welcome step

    // Contact form step - validate email and name
    if (showContactForm) {
      return formData.name.trim() && formData.email.trim() && validateEmail(formData.email);
    }

    // Question steps - check if answer is selected
    const questionGlobalIndex = currentStep - 2;
    if (questionGlobalIndex >= 0 && questionGlobalIndex < flatQuestions.length) {
      const { category, index } = categoryMap[questionGlobalIndex];
      return responses[category]?.[index] !== undefined;
    }

    return true;
  };

  const handleNext = async () => {
    // Clear any pending auto-advance timeout
    if (autoAdvanceTimeout) {
      clearTimeout(autoAdvanceTimeout);
      setAutoAdvanceTimeout(null);
    }

    // Track start of assessment when user moves from welcome page
    if (currentStep === 1) {
      trackStartAssessment();
    }

    // Show contact form after question 3 (step 4) if not already shown
    // This captures leads earlier in the funnel
    if (currentStep === 4 && !showContactForm && !formData.email) {
      setShowContactForm(true);
      setContactFormShownAt(Date.now());

      // Track that contact form was shown
      if (sessionId) {
        trackUserProgress({
          id: sessionId,
          name: formData.name || null,
          email: formData.email || null,
          progress: "Contact Form - Mid-Quiz",
          exit_point: "Contact Form After Q3",
          contact_form_shown_at: Date.now(),
          responses: {
            raw: responses,
            question_timestamps: questionTimestamps
          }
        });
      }
      return;
    }

    // If contact form is shown (after question 3), save the data and continue
    if (showContactForm && currentStep === 4) {
      // Track contact form completion
      const timeOnContactForm = contactFormShownAt
        ? Math.round((Date.now() - contactFormShownAt) / 1000)
        : 0;

      if (sessionId) {
        trackUserProgress({
          id: sessionId,
          name: formData.name,
          email: formData.email,
          company: formData.company || null,
          role: formData.role || null,
          progress: "Contact Form Completed - Continuing Quiz",
          exit_point: "Mid-Quiz Contact Form Completed",
          contact_form_completion_time: timeOnContactForm,
          responses: {
            raw: responses,
            question_timestamps: questionTimestamps
          }
        });
      }

      // Just save the contact data and continue with remaining questions
      setShowContactForm(false);
      setCurrentStep(prev => prev + 1);
      return;
    }

    // If all questions are completed, calculate scores and show results
    if (currentStep === totalSteps && !submitted) {
      // Calculate scores
      const formattedResponses = {};
      Object.keys(questions).forEach(category => {
        formattedResponses[category] = Object.values(responses[category] || {});
      });

      const { overallPercentage, categoryScores } = calculateScores(formattedResponses);
      const tier = getTierLabel(overallPercentage);

      // Store detailed answers for each question
      const detailedResponses = {};
      Object.entries(questions).forEach(([category, categoryQuestions]) => {
        detailedResponses[category] = categoryQuestions.map((question, index) => {
          const answerValue = responses[category]?.[index];
          const answerIndex = question.values.indexOf(answerValue);
          return {
            question: question.text,
            answer: answerIndex >= 0 ? question.options[answerIndex] : 'Not answered',
            score: answerValue || 0
          };
        });
      });

      const overallScore = overallPercentage;

      // Submit results to database
      setIsSubmitting(true);
      setSubmissionError(null);

      // Track completion with Meta Pixel
      trackCompletedAssessment(overallScore, tier);

      // Calculate final analytics data
      const finalTimeSpent = Math.round((Date.now() - sessionStartTime) / 1000);
      const questionsAnswered = Object.keys(responses).reduce((total, category) => 
        total + Object.keys(responses[category] || {}).length, 0
      );

      // Prepare data for submission to Supabase - Updated for new categories
      const submissionData = {
        name: formData.name,
        email: formData.email,
        company: formData.company || null,
        role: formData.role || null,
        overall_score: overallScore,
        tier,
        brand_strategy_score: categoryScores["Brand Positioning"] || 0,
        visual_identity_score: 0, // No longer used
        website_ux_score: categoryScores["Website Performance"] || 0,
        marketing_score: categoryScores["Marketing System"] || 0,
        responses: {
          raw: responses,
          detailed: detailedResponses
        },
        analytics_data: {
          time_spent_seconds: finalTimeSpent,
          questions_answered: questionsAnswered,
          completion_percentage: 100,
          device_info: {
            isMobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
            screenWidth: window.screen.width,
            userAgent: navigator.userAgent
          },
          session_start: sessionStartTime,
          session_end: Date.now(),
          question_timestamps: questionTimestamps,
          contact_form_shown_at: contactFormShownAt,
          contact_form_completed: !!formData.email
        }
      };

      console.log('Submitting form data to Supabase:', submissionData);

      try {
        // Store in Supabase
        const { success, error } = await storeFormSubmission(submissionData);

        if (!success) {
          console.error("Failed to store submission:", error);
          setSubmissionError("Failed to save your results. Please try again.");
        } else {
          console.log("Successfully stored submission in Supabase!");

          // Mark the user session as completed if we have a session ID
          if (sessionId) {
            await trackUserProgress({
              id: sessionId,
              name: formData.name,
              email: formData.email,
              company: formData.company || null,
              role: formData.role || null,
              progress: "Completed",
              exit_point: "Assessment Completed",
              completed: true,
              completion_time: new Date().toISOString(),
              final_analytics: {
                total_time_seconds: finalTimeSpent,
                questions_completed: questionsAnswered,
                final_score: overallScore,
                tier: tier
              }
            });
          }
        }

        // Show results
        setSubmitted({
          name: formData.name,
          overallScore,
          categoryScores,
          tier,
          formData,
          responses,
          detailedResponses
        });

      } catch (err) {
        console.error("Error submitting form:", err);
        setSubmissionError("An unexpected error occurred. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Just move to next question
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    // Clear any pending auto-advance timeout
    if (autoAdvanceTimeout) {
      clearTimeout(autoAdvanceTimeout);
      setAutoAdvanceTimeout(null);
    }

    if (showContactForm) {
      setShowContactForm(false);
    } else {
      setCurrentStep(prev => Math.max(1, prev - 1));
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-dark-700 py-6 md:py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ResultsSection {...submitted} submissionError={submissionError} />
        </div>
      </div>
    );
  }

  const renderStepContent = () => {
    if (showContactForm) {
      return (
        <ContactStep
          formData={formData}
          onFormDataChange={setFormData}
          showResultsHeading={false}
        />
      );
    }

    if (currentStep === 1) {
      return <WelcomeStep />;
    }

    // Question steps
    const questionGlobalIndex = currentStep - 2;
    const question = flatQuestions[questionGlobalIndex];
    const { category, index } = categoryMap[questionGlobalIndex];

    return (
      <QuestionStep
        question={question}
        selectedAnswer={responses[category]?.[index]}
        onAnswerChange={(value) => handleAnswerChange(questionGlobalIndex, value)}
        questionNumber={questionGlobalIndex + 1}
        totalQuestions={flatQuestions.length}
        category={question.category}
      />
    );
  };

  const getNextButtonText = () => {
    if (currentStep === 1) {
      return (
        <div className="flex items-center justify-center">
          Start Free Brand Check
          <FiArrowRight className="ml-2" />
        </div>
      );
    }
    if (showContactForm) return "Continue to Questions";
    if (currentStep === totalSteps) return isSubmitting ? "Calculating..." : "Get My Results";
    return "Continue";
  };

  // Calculate progress percentage
  // When contact form is shown at step 4, it's ~36% through (4/11 steps)
  const progressPercentage = showContactForm && currentStep === 4
    ? 36
    : (currentStep / totalSteps) * 100;

  // Determine if this is a question step
  const isQuestionStep = currentStep > 1 && !showContactForm;

  return (
    <AnimatePresence mode="wait">
      <TypeformStep
        key={showContactForm ? 'contact' : currentStep}
        currentStep={currentStep}
        totalSteps={totalSteps}
        progressPercentage={progressPercentage}
        onNext={handleNext}
        onPrev={handlePrev}
        canProceed={canProceed() && !isSubmitting}
        isLastStep={showContactForm || currentStep === totalSteps}
        nextButtonText={getNextButtonText()}
        isSubmitting={isSubmitting}
        isQuestionStep={isQuestionStep}
      >
        {renderStepContent()}
      </TypeformStep>
    </AnimatePresence>
  );
}

function App() {
  return (
    <HashRouter>
      <Routes>
        <Route path="/" element={<BrandHealthCheck />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </HashRouter>
  );
}

export default App;